import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import SideTooltip from '../../../components/SideTooltip'
import { Coin } from '../../../icons/Coin'
import ContextUsageRing from './ContextUsageRing'
import { getContextModes } from '../../../api/chat'
import type { AttachedFileMeta, ContextModesResponse, ContextUsage, SessionUsageResponse } from '../../../api/chat'

// PR3 (TZ_context_window): источник правды для набора режимов/множителей —
// GET /chat/context-modes (DB-конфигурация, см. app/services/context_modes.py
// на бэке). Константы ниже — ОФЛАЙН-ФОЛЛБЭК на случай ошибки/недоступности
// запроса (сеть, 5xx) — используются как раньше, ДО этого PR, когда бэк был
// единственным источником. Держать в синхроне с бэком вручную: автоматической
// сверки нет, это намеренно упрощённый fallback, а не зеркало БД.
const CONTEXT_STEPS = ['10k', '15k', '20k', '25k', '30k', 'max'] as const
export type ContextMode = (typeof CONTEXT_STEPS)[number]

const CONTEXT_LABELS: Record<ContextMode, string> = {
	'10k': '10K',
	'15k': '15K',
	'20k': '20K',
	'25k': '25K',
	'30k': '30K',
	'max': '∞',
}

const CONTEXT_DESCRIPTIONS: Record<ContextMode, { titleKey: string; subKey: string; noteKey?: string }> = {
	'10k': { titleKey: 'chat.context.10k.title', subKey: 'chat.context.10k.sub' },
	'15k': { titleKey: 'chat.context.15k.title', subKey: 'chat.context.15k.sub' },
	'20k': { titleKey: 'chat.context.20k.title', subKey: 'chat.context.20k.sub' },
	'25k': { titleKey: 'chat.context.25k.title', subKey: 'chat.context.25k.sub' },
	'30k': { titleKey: 'chat.context.30k.title', subKey: 'chat.context.30k.sub' },
	// noteKey — доп. красный блок-примечание внутри описания (слайдится вместе с ним).
	'max': { titleKey: 'chat.context.max.title', subKey: 'chat.context.max.sub', noteKey: 'chat.context.max.note' },
}

// Fallback, источник правды — GET /chat/context-modes (TZ_context_infinity.md
// раздел 6). Значения — снимок с прода на 25.08.2026 (админка меняла их
// 24.08 в 18:21: 15k 1.2->1.3, 20k 1.5->1.6, 25k 1.8->1.9, 30k 2.0->2.2, см.
// п.1.6 диагностики ТЗ) — используются ТОЛЬКО пока GET /chat/context-modes
// не ответил (сеть/5xx) или для моделей без provider/backendModel. Не
// синхронизируются с БД автоматически: если админ снова поправит множители
// в админке, этот фоллбэк устареет до следующей ручной правки — это
// приемлемо, т.к. в норме он никогда не виден (перекрывается ответом бэка
// за доли секунды после маунта).
export const CONTEXT_MULTIPLIERS: Record<ContextMode, number> = {
	'10k': 1.0,
	'15k': 1.3,
	'20k': 1.6,
	'25k': 1.9,
	'30k': 2.2,
	'max': 1.0,
}

// Модели с динамическим («тяжёлый хвост») списанием: итог = max(базовая цена,
// фактическая себестоимость токенов). У Pro/reasoning-моделей output (вкл.
// reasoning-токены) дорогой, и реальное списание почти всегда выше базовой
// цены из превью — поэтому в UI показываем её как минимум («от N»).
// Для всех остальных моделей в фикс. режимах памяти (10K..30K) цена строго
// фиксированная. 31.07.2026 набор на день опустошали («фикс. для всех»),
// решение отменено в тот же день — динамика Pro-моделей возвращена, «от»
// продублирован на карточках моделей (ModelCard).
//
// Источник правды — DYNAMIC_PRICED_MODELS в backend/app/api/v1/chat.py; бэк
// отдаёт готовый флаг в GET /chat/model-pricing → `dynamic_priced`. Этот
// список — локальный fallback, когда флага нет. Держать в синхроне с бэком.
export const DYNAMIC_PRICED_MODELS = new Set<string>([
	'gpt-5.2-pro', 'gpt-5.4-pro', 'gpt-5.5-pro', 'gpt-5-pro',
	'gpt-5.2-codex', 'gpt-5-codex', 'gpt-5.1-codex', 'gpt-5.1-codex-mini', 'gpt-5.1-codex-max',
])

export function isDynamicPriced(modelId?: string | null): boolean {
	return !!modelId && DYNAMIC_PRICED_MODELS.has(modelId)
}

const ANIM_DURATION = 300

interface ContextButtonProps {
	contextMode: ContextMode
	onChange: (mode: ContextMode) => void
	baseCost?: number
	// id выбранной модели — для пометки «от N» у динамически тарифицируемых
	// (Pro/reasoning) моделей, где итог обычно выше базовой цены.
	modelId?: string | null
	// Провайдер + backend-id модели — нужны для GET /chat/context-modes
	// (витрина режимов ИМЕННО для этой модели: allowed_modes/цены с учётом
	// точечных model_context_overrides). backendModel — если у карточки
	// модели свой backend-id, отличный от modelId (см. Chat.tsx). Без
	// provider запрос не летит — компонент остаётся на офлайн-фоллбэке.
	provider?: string | null
	backendModel?: string | null
	// Флаг `dynamic_priced` из GET /chat/model-pricing. Если пришёл — он
	// главнее локального DYNAMIC_PRICED_MODELS (бэк = источник правды, иначе
	// UI и списание могут разойтись). undefined/null → локальный fallback.
	dynamicPriced?: boolean | null
	// Свежий срез заполнения контекста. null/undefined до первой загрузки.
	usage?: ContextUsage | null
	// Полная разбивка + список файлов. Показывается в popover при открытии.
	usageDetail?: SessionUsageResponse | null
	// Внешний вид триггера:
	//   'pill'    (default) — кнопка с иконкой и текстом «Память 10K»
	//                          для размещения в pills row рядом с input.
	//   'compact'           — компактный кружок ~32px с label режима внутри
	//                          и ring-progress по краю. Размещается обычно
	//                          в footer'е под input'ом.
	variant?: 'pill' | 'compact'
	// TZ_context_infinity.md раздел 6 (задача 5): бэйдж «Стоимость сообщения»
	// в Chat.tsx считал цену по локальной CONTEXT_MULTIPLIERS, а поповер этой
	// кнопки — по множителю из GET /chat/context-modes (источник правды,
	// учитывает точечные multiplier_overrides модели). Расхождение — тот же
	// класс бага, что уже разбирался 30.07 (бейдж показывает не то, что
	// спишется). Вместо второго запроса в родителе — отдаём уже резолвленный
	// множитель наверх колбэком, родитель использует его для своего бейджа.
	onMultiplierResolved?: (multiplier: number) => void
	// Витрина режимов этой модели целиком (пороги min_balance, подписи, текст плашки) — родителю для стопа до отправки
	// (владелец 15.10, Chat.tsx → modeShort). null — витрина не загружена (офлайн-фоллбэк).
	onModesResolved?: (modes: ContextModesResponse | null) => void
}

function formatTokens(n: number): string {
	// 8240 → "8.2k", 12000 → "12k", 900 → "900", 0 → "0"
	if (n < 1000) return String(n)
	const k = n / 1000
	if (k >= 100) return `${Math.round(k)}k`
	if (k >= 10) return `${k.toFixed(0)}k`
	return `${k.toFixed(1)}k`
}

function fileShortLabel(file: AttachedFileMeta): string {
	const name = file.filename || 'file'
	if (name.length <= 32) return name
	const ext = name.split('.').pop()
	const base = name.slice(0, 24)
	return `${base}…${ext ? `.${ext}` : ''}`
}

export default function ContextButton({
	contextMode,
	onChange,
	baseCost,
	modelId,
	provider,
	backendModel,
	dynamicPriced,
	usage,
	usageDetail,
	variant = 'pill',
	onMultiplierResolved,
	onModesResolved,
}: ContextButtonProps) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [pos, setPos] = useState<{ bottom: number; left: number } | null>(null)

	// Витрина режимов ИМЕННО для этой модели (allowed_modes может быть
	// подмножеством у Pro-моделей — см. model_context_overrides на бэке).
	// null = офлайн-фоллбэк (ошибка запроса ИЛИ ещё грузится ИЛИ нет
	// provider/backendModel) — тогда используем полный набор CONTEXT_STEPS,
	// как было ДО этого PR.
	const [remoteModes, setRemoteModes] = useState<ContextModesResponse | null>(null)
	useEffect(() => {
		const modelForApi = backendModel || modelId
		if (!provider || !modelForApi) {
			setRemoteModes(null)
			return
		}
		let cancelled = false
		getContextModes(provider, modelForApi).then((res) => {
			if (!cancelled) setRemoteModes(res)
		})
		return () => { cancelled = true }
	}, [provider, backendModel, modelId])
	useEffect(() => {
		onModesResolved?.(remoteModes)
	}, [remoteModes, onModesResolved])

	// Разрешённые режимы этой модели, в порядке sort_order с бэка. Пустой
	// remoteModes (офлайн-фоллбэк) → полный CONTEXT_STEPS, всё разрешено —
	// прежнее поведение до PR3 (сервер тогда клампинг ещё не делал).
	const allowedSteps: ContextMode[] = remoteModes
		? (remoteModes.modes.filter((m) => m.allowed).map((m) => m.mode_key) as ContextMode[])
		: [...CONTEXT_STEPS]
	const effectiveSteps: ContextMode[] = allowedSteps.length > 0 ? allowedSteps : [...CONTEXT_STEPS]

	// «2-режимный Pro» — allowed_modes ограничен до двух пунктов (типовой
	// случай: ['10k','max']). Показываем сегментированный тумблер вместо
	// непрерывного слайдера (слайдер на 2 позиции читается как «сломанный»)
	// и поясняющую подсказку под контролом (chat.context.proModeNote).
	const isTwoModeRestricted = effectiveSteps.length === 2
	const isSingleModeRestricted = effectiveSteps.length === 1

	const [current, setCurrent] = useState(contextMode)
	const [leaving, setLeaving] = useState<ContextMode | null>(null)
	const [dir, setDir] = useState<'next' | 'prev'>('next')
	const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

	const btnRef = useRef<HTMLButtonElement>(null)
	const popoverRef = useRef<HTMLDivElement>(null)
	// Рефы на обёртку + слайды описания — высота desc-wrap подгоняется по контенту
	// (режим ∞ выше из-за note) императивно, без state.
	const descWrapRef = useRef<HTMLDivElement>(null)
	const descEnterRef = useRef<HTMLDivElement>(null)
	const descLeaveRef = useRef<HTMLDivElement>(null)

	// Слайд-стейт подстраивается ВО ВРЕМЯ рендера (паттерн React «adjusting
	// state when props change», см. react.dev/you-might-not-need-an-effect) —
	// без setState-в-эффекте и каскадных ре-рендеров.
	if (contextMode !== current) {
		const prevIdx = CONTEXT_STEPS.indexOf(current)
		const newIdx = CONTEXT_STEPS.indexOf(contextMode)
		setDir(newIdx > prevIdx ? 'next' : 'prev')
		setLeaving(current)
		setCurrent(contextMode)
	}

	// Очистка «уходящего» слайда по концу анимации: setState только в колбэке
	// таймера (асинхронно — допустимо). Перезапуск при быстрых переключениях.
	useEffect(() => {
		if (!leaving) return
		if (leaveTimer.current) clearTimeout(leaveTimer.current)
		leaveTimer.current = setTimeout(() => setLeaving(null), ANIM_DURATION)
		return () => { if (leaveTimer.current) clearTimeout(leaveTimer.current) }
	}, [leaving, current])

	// Высота desc-wrap = по контенту описания: у ∞ есть red-note внутри → выше.
	// Берём max(входящий, уходящий), чтобы overflow:hidden не обрезал ничего во
	// время перехода; CSS-transition по min-height даёт плавный рост/спад в такт слайду.
	useLayoutEffect(() => {
		if (!open || !descWrapRef.current) return
		const eh = descEnterRef.current?.scrollHeight ?? 0
		const lh = descLeaveRef.current?.scrollHeight ?? 0
		// Императивно через ref (без setState — иначе лишний ре-рендер и правило
		// react-hooks/set-state-in-effect). CSS-transition анимирует рост/спад.
		descWrapRef.current.style.minHeight = `${Math.max(54, eh, lh)}px`
	}, [open, current, leaving])

	const POPOVER_WIDTH = 256
	useLayoutEffect(() => {
		if (!open || !btnRef.current) return
		const r = btnRef.current.getBoundingClientRect()
		const idealLeft = r.left + r.width / 2 - POPOVER_WIDTH / 2
		const left = Math.max(8, Math.min(idealLeft, window.innerWidth - POPOVER_WIDTH - 8))
		setPos({ bottom: window.innerHeight - r.top + 10, left })
	}, [open])

	useEffect(() => {
		if (!open) return
		const handler = (e: MouseEvent) => {
			const target = e.target as Node
			if (
				popoverRef.current && !popoverRef.current.contains(target) &&
				btnRef.current && !btnRef.current.contains(target)
			) setOpen(false)
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [open])

	useEffect(() => {
		if (!open) return
		const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
		document.addEventListener('keydown', handler)
		return () => document.removeEventListener('keydown', handler)
	}, [open])

	const handleSliderChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onChange(effectiveSteps[parseInt(e.target.value)] as ContextMode)
		},
		[onChange, effectiveSteps],
	)

	const label = CONTEXT_LABELS[contextMode]

	// Множитель — из витрины бэка для этой модели (учитывает точечные
	// multiplier_overrides), офлайн-фоллбэк — локальная константа.
	//
	// Режим «∞» (2026-09-11, ступени): списание идёт по ступени, в которую
	// уложился фактический вход (≤10k — как 10k, ≤15k — как 15k, …), поэтому
	// нижняя граница «от N» — по МИНИМАЛЬНОМУ множителю среди разрешённых
	// модели режимов, а не по множителю самого «∞» (тот — потолок).
	const remoteMultiplier = contextMode === 'max' && remoteModes
		? remoteModes.modes.filter((m) => m.allowed).reduce<number | undefined>(
			(min, m) => (min == null || m.multiplier < min ? m.multiplier : min), undefined,
		)
		: remoteModes?.modes.find((m) => m.mode_key === contextMode)?.multiplier
	const activeMultiplier = remoteMultiplier ?? CONTEXT_MULTIPLIERS[contextMode]
	useEffect(() => {
		onMultiplierResolved?.(activeMultiplier)
	}, [activeMultiplier, onMultiplierResolved])
	const costN = baseCost != null ? Math.round(baseCost * activeMultiplier) : null
	// Pro/reasoning-модели тарифицируются по факту токенов → реальное списание
	// почти всегда выше базовой цены. В превью показываем её как минимум («от N»).
	//
	// Решение владельца 2026-09-03: у dynamic-моделей «от N» показывается во
	// ВСЕХ режимах, включая «∞» — раньше там стоял голый символ '∞' без числа,
	// и правка множителя в админке визуально ничего не меняла. N = базовая
	// цена × множитель режима (нижняя граница списания, см.
	// compute_credits_spent на бэке). У обычных (fixed) моделей режим «∞»
	// по-прежнему '∞': там нет своей цены режима, списание идёт по факту.
	const modelIsDynamic = dynamicPriced ?? isDynamicPriced(modelId)
	const priceIsMinimum = costN != null && (modelIsDynamic || contextMode === 'max')
	const costDisplay = costN == null
		? null
		: modelIsDynamic
			? t('chat.messageCostFrom', { value: costN })
			: contextMode === 'max'
				? '∞'
				: String(costN)

	const popover = open && pos
		? createPortal(
			<div
				ref={popoverRef}
				className="ctx-btn__popover wmp__dropdown"
				style={{ position: 'fixed', bottom: pos.bottom, left: pos.left, width: POPOVER_WIDTH }}
			>
				<div className="ctx-btn__popover-header">
					<span className="ctx-btn__popover-title">{t('chat.context.title')}</span>
					<span className="ctx-btn__popover-value">{label}</span>
				</div>

				<div className="ctx-btn__desc-wrap" ref={descWrapRef}>
					{leaving && (
						<div
							key={`leave-${leaving}`}
							ref={descLeaveRef}
							className={`ctx-btn__desc ctx-btn__desc--out ctx-btn__desc--out-${dir}`}
						>
							<span className="ctx-btn__desc-title">{t(CONTEXT_DESCRIPTIONS[leaving].titleKey)}</span>
							<span className="ctx-btn__desc-sub">{t(CONTEXT_DESCRIPTIONS[leaving].subKey)}</span>
							{CONTEXT_DESCRIPTIONS[leaving].noteKey && (
								<div className="ctx-btn__desc-note">{t(CONTEXT_DESCRIPTIONS[leaving].noteKey!)}</div>
							)}
						</div>
					)}
					<div
						key={`enter-${current}`}
						ref={descEnterRef}
						className={`ctx-btn__desc ctx-btn__desc--in ctx-btn__desc--in-${dir}`}
					>
						<span className="ctx-btn__desc-title">{t(CONTEXT_DESCRIPTIONS[current].titleKey)}</span>
						<span className="ctx-btn__desc-sub">{t(CONTEXT_DESCRIPTIONS[current].subKey)}</span>
						{CONTEXT_DESCRIPTIONS[current].noteKey && (
							<div className="ctx-btn__desc-note">
								{/* TZ_context_infinity.md п.4.3: обещание безлимита заменено на
								    честную формулировку с фактическим числом — но только когда
								    оно у нас реально есть (usage приходит после первого ответа
								    в сессии; для новой сессии остаётся общая формулировка). */}
								{current === 'max' && usage && (
									<>{t('chat.context.max.noteLimit', { limit: formatTokens(usage.limit) })}{' '}</>
								)}
								{t(CONTEXT_DESCRIPTIONS[current].noteKey!)}
								{/* Ступени «∞» (2026-09-11): цена зависит от того, в какой
								    лимит уложился запрос — показываем шкалу из витрины
								    (только разрешённые модели режимы, с её оверрайдами). */}
								{current === 'max' && remoteModes && (() => {
									const tiers = remoteModes.modes.filter((m) => m.allowed)
									if (tiers.length < 2) return null
									return (
										<div className="ctx-btn__desc-tiers">
											{t('chat.context.max.tiersLabel')}{' '}
											{tiers.map((m, i) => (
												<span key={m.mode_key}>
													{i > 0 && ' · '}
													{m.token_limit == null
														? t('chat.context.max.tierAbove')
														: t('chat.context.max.tierUpTo', { limit: formatTokens(m.token_limit) })}
													{' ×'}{m.multiplier}
												</span>
											))}
										</div>
									)
								})()}
							</div>
						)}
					</div>
				</div>

				{/* Ровно 1 разрешённый режим — контрол не нужен, показываем
				    статичную подпись (напр. модель жёстко зафиксирована на '10k'). */}
				{isSingleModeRestricted ? (
					<div className="ctx-btn__single-mode">
						<span className="ctx-btn__single-mode-label">{CONTEXT_LABELS[effectiveSteps[0]]}</span>
					</div>
				) : isTwoModeRestricted ? (
					// Ровно 2 разрешённых режима (типовой случай — Pro-модели,
					// allowed_modes=['10k','max']) — сегментированный тумблер вместо
					// непрерывного слайдера: 2-позиционный range-слайдер читается
					// как «сломанный» UI, тумблер однозначен.
					<div className="ctx-btn__segmented" role="group" aria-label={t('chat.context.title')}>
						{effectiveSteps.map((step) => (
							<button
								key={step}
								type="button"
								className={`ctx-btn__segmented-option${step === contextMode ? ' ctx-btn__segmented-option--active' : ''}`}
								onClick={() => onChange(step)}
							>
								{CONTEXT_LABELS[step]}
							</button>
						))}
					</div>
				) : (
					<div className="ctx-btn__slider-wrap">
						<input
							type="range"
							className="context-slider-input"
							min={0}
							max={effectiveSteps.length - 1}
							step={1}
							value={Math.max(0, effectiveSteps.indexOf(contextMode))}
							onChange={handleSliderChange}
							style={{
								'--progress': `${(Math.max(0, effectiveSteps.indexOf(contextMode)) / (effectiveSteps.length - 1)) * 100}%`,
							} as React.CSSProperties}
						/>
						<div className="context-slider-labels">
							{effectiveSteps.map((step) => (
								<span
									key={step}
									className={step === contextMode ? 'active' : ''}
									onClick={() => onChange(step)}
								>
									{CONTEXT_LABELS[step]}
								</span>
							))}
						</div>
					</div>
				)}

				{isTwoModeRestricted && (
					<div className="ctx-btn__pro-note">{t('chat.context.proModeNote')}</div>
				)}

				{usage && (
					<div className="ctx-btn__usage" data-level={
						usage.percent >= 90 ? 'high' : usage.percent >= 70 ? 'mid' : 'low'
					}>
						<div className="ctx-btn__usage-row">
							<span className="ctx-btn__usage-label">{t('chat.context.usageFilled')}</span>
							<span className="ctx-btn__usage-value">
								{formatTokens(usage.used)} / {formatTokens(usage.limit)}
								<span className="ctx-btn__usage-percent">·&nbsp;{usage.percent}%</span>
							</span>
						</div>
						<div className="ctx-btn__usage-bar">
							<span
								className="ctx-btn__usage-bar-fill"
								style={{ width: `${Math.max(0, Math.min(100, usage.percent))}%` }}
							/>
						</div>
						{/* TZ_context_infinity.md раздел 4, п.4.1: вытеснение больше не
						    молчаливое — показываем факт и число не поместившихся сообщений
						    прямо в поповере режима памяти, а не только разовой плашкой. */}
						{usage.trimmed && usage.dropped_messages ? (
							<div className="ctx-btn__usage-hint">
								{t('chat.context.droppedMessages', { count: usage.dropped_messages })}
							</div>
						) : null}
						{usageDetail && (
							<div className="ctx-btn__usage-breakdown">
								{usageDetail.summary?.present && usageDetail.summary.tokens > 0 && (
									<span>{t('chat.context.summary')}&nbsp;{formatTokens(usageDetail.summary.tokens)}</span>
								)}
								{usageDetail.breakdown.history_text > 0 && (
									<span>{t('chat.context.text')}&nbsp;{formatTokens(usageDetail.breakdown.history_text)}</span>
								)}
								{usageDetail.breakdown.history_files > 0 && (
									<span>{t('chat.context.files')}&nbsp;{formatTokens(usageDetail.breakdown.history_files)}</span>
								)}
								{usageDetail.breakdown.history_images > 0 && (
									<span>{t('chat.context.images')}&nbsp;{formatTokens(usageDetail.breakdown.history_images)}</span>
								)}
							</div>
						)}
						{usageDetail && usageDetail.files.length > 0 && (
							<div className="ctx-btn__usage-files">
								{usageDetail.files.slice(0, 5).map((f, i) => (
									<div key={`${f.filename}-${i}`} className="ctx-btn__usage-file" title={f.filename}>
										<span className="ctx-btn__usage-file-icon" aria-hidden="true">📎</span>
										<span className="ctx-btn__usage-file-name">{fileShortLabel(f)}</span>
									</div>
								))}
								{usageDetail.files.length > 5 && (
									<div className="ctx-btn__usage-file ctx-btn__usage-file--more">
										+{usageDetail.files.length - 5}
									</div>
								)}
							</div>
						)}
						{usage.percent >= 80 && contextMode !== 'max' && (
							<div className="ctx-btn__usage-hint">
								{t('chat.context.upgradeHint')}
							</div>
						)}
					</div>
				)}

				{costDisplay != null && (
					<div className="ctx-btn__popover-footer">
						<span className="ctx-btn__popover-cost-label">{t('chat.messageCost')}</span>
						<span className="ctx-btn__popover-cost">
							<span
								key={contextMode}
								className={`ctx-btn__popover-cost-value${contextMode === 'max' ? ' ctx-btn__popover-cost-value--inf' : ''}`}
							>
								{costDisplay}
							</span>
							<Coin size={15} className="generate-cost-icon" />
						</span>
					</div>
				)}
				{priceIsMinimum && (
					<div className="ctx-btn__usage-hint ctx-btn__price-hint">
						{t('chat.context.dynamicPriceHint')}
					</div>
				)}
			</div>,
			document.body,
		)
		: null

	// Текст тултипа: при наличии данных — реальные цифры, иначе общий хинт.
	const tooltipText = usage
		? `${t('chat.context.memory')} ${formatTokens(usage.used)} / ${formatTokens(usage.limit)} (${usage.percent}%)`
		: t('chat.context.tooltip')

	// Compact: маленький круглый индикатор с label режима внутри + ring по краю.
	// Используется в footer'е чата справа от «Стоимость сообщения».
	if (variant === 'compact') {
		return (
			<>
				{popover}
				<SideTooltip text={tooltipText}>
					<button
						ref={btnRef}
						type="button"
						className={`ctx-btn ctx-btn--compact ${open ? 'ctx-btn--active' : ''}`}
						onClick={() => setOpen((v) => !v)}
						aria-expanded={open}
						aria-haspopup="true"
						aria-label={tooltipText}
					>
						<ContextUsageRing percent={usage?.percent} size={32} strokeWidth={2} />
						<span
							className={
								'ctx-btn__compact-label' +
								// Unicode '∞' имеет меньшую x-height чем цифры —
								// при одинаковом font-size выглядит мельче. Доп.
								// класс даёт ему увеличенный размер для визуального
								// баланса с метками '10K' / '20K' / etc.
								(contextMode === 'max' ? ' ctx-btn__compact-label--inf' : '')
							}
						>
							{label}
						</span>
					</button>
				</SideTooltip>
			</>
		)
	}

	// Pill (default) — текущий вариант с текстом «Память 10K» для pills row.
	return (
		<>
			{popover}
			<SideTooltip text={tooltipText}>
				<button
					ref={btnRef}
					type="button"
					className={`ctx-btn ${open ? 'ctx-btn--active' : ''}`}
					onClick={() => setOpen((v) => !v)}
					aria-expanded={open}
					aria-haspopup="true"
				>
					<ContextUsageRing percent={usage?.percent} size={12} strokeWidth={1.6} />
					<span>{t('chat.context.memory')} {label}</span>
				</button>
			</SideTooltip>
		</>
	)
}
